import React from 'react';
import { User, Mail, Phone, MapPin, Calendar, Shield, Bell, CreditCard, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { mockUser, formatCurrency } from '@/utils/mockData';

export default function ProfilePage() {
  return (
    <div className="max-w-4xl mx-auto" data-testid="profile-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold font-heading text-slate-900">Profile & Settings</h1>
        <p className="text-slate-600 mt-1">Manage your account information and preferences</p>
      </div>

      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 mb-6">
        <div className="flex items-center gap-6">
          <div className="w-24 h-24 rounded-full bg-brand-blue text-white flex items-center justify-center text-3xl font-bold">
            {mockUser.firstName.charAt(0)}{mockUser.lastName.charAt(0)}
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-bold font-heading text-slate-900">{mockUser.firstName} {mockUser.lastName}</h2>
            <p className="text-slate-600">{mockUser.email}</p>
            <div className="flex items-center gap-4 mt-2">
              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700">{mockUser.tier} Tier</span>
              <span className="text-sm text-slate-600">Member since {new Date(mockUser.joinDate).toLocaleDateString('en-NG', {year: 'numeric', month: 'short'})}</span>
            </div>
          </div>
          <Button data-testid="edit-profile-btn" className="bg-brand-blue text-white hover:bg-blue-700 rounded-xl transition-all active:scale-95">
            Edit Profile
          </Button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
          <h3 className="font-bold font-heading text-slate-900 mb-4">Personal Information</h3>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-slate-400" />
              <div>
                <p className="text-sm text-slate-600">Email</p>
                <p className="font-medium text-slate-900">{mockUser.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Phone className="w-5 h-5 text-slate-400" />
              <div>
                <p className="text-sm text-slate-600">Phone</p>
                <p className="font-medium text-slate-900">{mockUser.phone}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <CreditCard className="w-5 h-5 text-slate-400" />
              <div>
                <p className="text-sm text-slate-600">Account Number</p>
                <p className="font-medium text-slate-900">{mockUser.accountNumber}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
          <h3 className="font-bold font-heading text-slate-900 mb-4">Security</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="font-medium text-slate-900">Two-Factor Auth</p>
                  <p className="text-sm text-slate-600">Extra security layer</p>
                </div>
              </div>
              <Switch data-testid="2fa-toggle" />
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Bell className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="font-medium text-slate-900">Notifications</p>
                  <p className="text-sm text-slate-600">Transaction alerts</p>
                </div>
              </div>
              <Switch data-testid="notifications-toggle" defaultChecked />
            </div>
            <Button data-testid="change-password-btn" variant="outline" className="w-full rounded-xl h-12 mt-4">
              Change Password
            </Button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 mb-6">
        <h3 className="font-bold font-heading text-slate-900 mb-4">Account Limits</h3>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="p-4 rounded-xl bg-slate-50">
            <p className="text-sm text-slate-600 mb-1">Daily Transfer Limit</p>
            <p className="text-lg font-bold text-slate-900">₦500,000</p>
          </div>
          <div className="p-4 rounded-xl bg-slate-50">
            <p className="text-sm text-slate-600 mb-1">Monthly Limit</p>
            <p className="text-lg font-bold text-slate-900">₦5,000,000</p>
          </div>
          <div className="p-4 rounded-xl bg-slate-50">
            <p className="text-sm text-slate-600 mb-1">Maximum Balance</p>
            <p className="text-lg font-bold text-slate-900">₦10,000,000</p>
          </div>
        </div>
        <p className="text-sm text-slate-600 mt-4">Upgrade to Premium Tier to increase your limits</p>
      </div>

      <div className="bg-white rounded-2xl p-6 shadow-sm border border-red-200">
        <h3 className="font-bold font-heading text-red-600 mb-2">Danger Zone</h3>
        <p className="text-sm text-slate-600 mb-4">Permanent actions that cannot be undone</p>
        <div className="flex gap-3">
          <Button data-testid="deactivate-account-btn" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50 rounded-xl">
            Deactivate Account
          </Button>
          <Button data-testid="delete-account-btn" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50 rounded-xl">
            Delete Account
          </Button>
        </div>
      </div>
    </div>
  );
}
